/**
 * Medium-Style Navigation Animation System
 * Works on both desktop and mobile
 */

class NavigationAnimations {
    constructor() {
        this.header = document.querySelector('.site-header');
        if (!this.header) return;

        // Configuration
        this.config = {
            scrollThreshold: 10,      // Minimum scroll to trigger
            hideShowDiff: 5,         // Minimum scroll difference to trigger hide/show
            scrolledClass: 50,       // When to add 'scrolled' class
            mobileScrolledClass: 30  // Lower threshold for mobile
        };

        // State
        this.state = {
            lastScrollY: 0,
            isHidden: false,
            userInteracted: false,
            isMobile: window.innerWidth < 992
        };

        this.init();
    }

    init() {
        // Initialize scroll animations for all devices
        this.checkViewport();
        this.attachResizeListener();
        this.attachScrollListener(); // Now works on mobile too
        this.attachInteractionListeners();
        this.initializeState();
    }

    checkViewport() {
        this.state.isMobile = window.innerWidth < 992;
    }

    attachResizeListener() {
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.checkViewport();
            }, 250);
        });
    }

    attachScrollListener() {
        // Remove any existing listener first
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }

        let lastKnownScrollY = 0;
        let ticking = false;

        const update = () => {
            const scrollY = window.pageYOffset;
            const diff = scrollY - lastKnownScrollY;

            // Use different threshold for mobile vs desktop
            const scrolledThreshold = this.state.isMobile ? this.config.mobileScrolledClass : this.config.scrolledClass;

            // Add/remove scrolled class for styling
            if (scrollY > scrolledThreshold) {
                this.header.classList.add('scrolled');
            } else {
                this.header.classList.remove('scrolled');
            }

            // Only hide/show if we've scrolled enough
            if (Math.abs(diff) < this.config.hideShowDiff) {
                lastKnownScrollY = scrollY;
                ticking = false;
                return;
            }

            // Scrolling down - hide navigation
            if (diff > 0 && scrollY > this.config.scrollThreshold) {
                if (!this.state.isHidden) {
                    this.header.classList.add('nav-up');
                    this.state.isHidden = true;
                    this.triggerHapticFeedback();
                }
            }
            // Scrolling up - show navigation
            else if (diff < 0) {
                if (this.state.isHidden) {
                    this.header.classList.remove('nav-up');
                    this.state.isHidden = false;
                    this.triggerHapticFeedback();
                }
            }

            lastKnownScrollY = scrollY;
            ticking = false;
        };

        this.scrollHandler = () => {
            if (!ticking) {
                requestAnimationFrame(update);
                ticking = true;
            }
        };

        window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    attachInteractionListeners() {
        // Mark user as having interacted after first touch/click/key press
        const markUserInteracted = () => {
            this.state.userInteracted = true;
            // Remove listeners after first interaction
            document.removeEventListener('click', markUserInteracted, true);
            document.removeEventListener('touchstart', markUserInteracted, true);
            document.removeEventListener('keydown', markUserInteracted, true);
        };

        // Listen for user interaction
        document.addEventListener('click', markUserInteracted, true);
        document.addEventListener('touchstart', markUserInteracted, { passive: true, capture: true });
        document.addEventListener('keydown', markUserInteracted, true);
    }

    initializeState() {
        this.state.lastScrollY = window.pageYOffset;

        // Clean up any animation classes
        this.header.classList.remove('nav-up', 'nav-down', 'hidden', 'visible');

        // Set initial scrolled state
        if (this.state.lastScrollY > this.config.scrolledClass) {
            this.header.classList.add('scrolled');
        } else {
            this.header.classList.remove('scrolled');
        }

        // Reset hidden state
        this.state.isHidden = false;
    }

    triggerHapticFeedback() {
        // Trigger haptic feedback on supported devices
        // Only vibrate if user has interacted with the page
        if ('vibrate' in navigator && this.state.userInteracted) {
            try {
                navigator.vibrate(10);
            } catch (e) {
                // Silently fail if vibration is blocked
            }
        }
    }

    // Public method to force show navigation
    forceShow() {
        this.header.classList.remove('nav-up');
        this.state.isHidden = false;
    }

    // Public method to force hide navigation
    forceHide() {
        this.header.classList.add('nav-up');
        this.state.isHidden = true;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.navigationAnimations = new NavigationAnimations();
    });
} else {
    window.navigationAnimations = new NavigationAnimations();
}